import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Wrench } from "lucide-react";
import { http, apiError } from "@/lib/shop";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UsePartDialog } from "./UsePartDialog";

export const AdminRepairs = ({ owner }) => {
  const [items, setItems] = useState(null);
  const [using, setUsing] = useState(null);
  const load = () => http.get("/repairs").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    try { await http.patch(`/repairs/${r._id}`, { status }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this request?")) return;
    await http.delete(`/repairs/${r._id}`); load();
  };

  if (items === null) return <p className="mt-6 text-sm text-slate-500">Loading…</p>;
  if (!items.length) return <p className="mt-6 text-sm text-slate-500" data-testid="admin-repairs-empty">No repair requests yet.</p>;

  return (
    <div className="mt-6 grid gap-3">
      {items.map((r) => (
        <div key={r._id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`admin-repair-${r._id}`}>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <p className="font-bold">{r.name} · <span className="text-brand-600">{r.service}</span></p>
              <p className="text-sm text-slate-600">{r.device} · {r.phone}{r.preferred_date ? ` · Wants: ${r.preferred_date}` : ""}</p>
              {r.notes && <p className="text-sm text-slate-500 mt-1">{r.notes}</p>}
              <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleString("en-GB")}</p>
            </div>
            <a href={`https://wa.me/${r.phone.replace(/\D/g, "").replace(/^0/, "44")}?text=${encodeURIComponent(`Hi ${r.name}, this is FONZ FIX4U about your ${r.device} repair.`)}`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-[#128C7E] hover:underline">Reply on WhatsApp</a>
            <button onClick={() => setUsing(r)} className="inline-flex items-center gap-1.5 rounded-full border-2 border-ink px-4 py-1.5 text-sm font-bold hover:bg-ink hover:text-white transition-colors" data-testid={`admin-repair-use-part-${r._id}`}><Wrench className="h-4 w-4" /> Use part</button>
            <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
              <SelectTrigger className="w-36" data-testid={`admin-repair-status-${r._id}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem><SelectItem value="done">Done</SelectItem></SelectContent>
            </Select>
            {owner && <button onClick={() => remove(r)} className="p-2 rounded-full hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
          </div>
          {r.parts_used?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2" data-testid={`admin-repair-parts-${r._id}`}>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 self-center">Parts used:</span>
              {r.parts_used.map((p) => (
                <span key={p.movement_id} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">{p.quantity} × {p.part_name}</span>
              ))}
            </div>
          )}
        </div>
      ))}
      <UsePartDialog repair={using} onClose={() => setUsing(null)} onDone={() => { setUsing(null); load(); }} />
    </div>
  );
};
